import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { courseSlug, formatShortDate } from '@app/shared'

import {
  useAssignLectureCourse,
  useCapturedLecture,
  useCourses,
  type CapturedLecture,
} from '../lib/data'
import { SkeletonRows } from '../components/Card'
import { BuildBadge, DataModeBadge } from '../components/HeaderBand'
import { LectureStatusChip } from '../components/school/Chrome'
import { clockLength } from '../lib/time'

// ============================================================================
// ONE RECORDING — where an unfiled lecture gets its course.
//
// WHY THIS SCREEN EXISTS AT ALL. The School screens address a lecture as
// /school/:courseCode/lecture/:n — through a course and a number within it. A recording from the
// PC's drop folder has NEITHER: the folder knows a filename and nothing else, so everything
// backfilled through it was, until now, unreachable by any URL in the app. It could be read
// through the API and nowhere else, which is the machine-readability rule holding in exactly one
// direction. This is the id-addressed page those recordings needed, and it is deliberately small:
// what the recording is, where it is in the pipeline, and the one write — its course.
//
// IT IS NOT THE STUDY SCREEN. Once a recording has a course and a transcript, 6c
// (SchoolLecture.tsx) is where the transcript, summary, flashcards and practice questions live,
// and this page links there rather than growing a second copy of them.
//
// MOBILE-FIRST, for the same reason /record is: the moment an orphan recording gets noticed is
// the moment it is uploaded, on a phone.
// ============================================================================

/** '' is the select's "no course" value — filing and unfiling are the same control. */
const NO_COURSE = ''

export function Recording() {
  const { id } = useParams<{ id: string }>()
  const { data: lecture, isPending, error } = useCapturedLecture(id)
  const { data: courses, error: coursesError } = useCourses()
  const assign = useAssignLectureCourse()

  // The picker is seeded from the lecture ONCE it arrives, and then belongs to the person using
  // it: re-seeding on every render would fight a selection mid-change.
  const [choice, setChoice] = useState<string | null>(null)
  useEffect(() => {
    if (lecture && choice === null) setChoice(lecture.courseCode ?? NO_COURSE)
  }, [lecture, choice])

  const current = lecture?.courseCode ?? NO_COURSE
  const changed = choice !== null && choice !== current

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <header className="flex flex-none flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-surface px-4 pb-4 pt-5 sm:px-[30px]">
        <div className="flex min-w-0 flex-col gap-[5px]">
          <Link
            to="/record"
            className="text-[11.5px] font-medium leading-none text-muted underline underline-offset-2"
          >
            Recordings
          </Link>
          <h1 className="truncate text-[16px] font-semibold leading-none tracking-[-0.01em]">
            {lecture?.title ?? 'Recording'}
          </h1>
        </div>
        <div className="ml-auto flex flex-none items-center gap-2">
          <DataModeBadge />
          <BuildBadge />
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[560px] flex-col gap-4 px-4 pb-10 pt-4 sm:px-[30px]">
        {isPending && (
          <div className="rounded-card border border-line bg-surface px-4 py-4 shadow-card">
            <SkeletonRows rows={2} />
          </div>
        )}

        {error && (
          <p className="rounded-row border border-danger-line bg-danger-fill px-3 py-[9px] text-[12px] leading-[1.45] text-danger text-pretty">
            {error instanceof Error ? error.message : 'This recording could not be read.'}
          </p>
        )}

        {!isPending && !error && !lecture && (
          <p className="rounded-card border border-line bg-surface px-4 py-4 text-[13px] leading-[1.5] text-ink-3 shadow-card text-pretty">
            There is no recording with that id. It may have been uploaded under a different
            account, or removed.{' '}
            <Link to="/record" className="underline underline-offset-2">
              Back to uploads
            </Link>
            .
          </p>
        )}

        {lecture && (
          <>
            <section className="flex flex-col gap-3 rounded-card border border-line bg-surface px-4 py-4 shadow-card">
              <div className="flex flex-wrap items-center gap-2">
                <LectureStatusChip status={lecture.status} />
                {lecture.audioLocation === 'archived_on_pc' && (
                  <span
                    className="rounded-pill bg-surface-4 px-[9px] py-1 text-[10.5px] font-semibold leading-none text-muted"
                    title="The original recording is archived on your desktop PC. The cloud copy was deleted after transcription, on purpose."
                  >
                    audio on desktop
                  </span>
                )}
                {lecture.audioLocation === 'cloud' && (
                  <span
                    className="rounded-pill bg-surface-4 px-[9px] py-1 text-[10.5px] font-semibold leading-none text-muted"
                    title="The recording is still in cloud storage waiting for the desktop worker. It moves to the PC and is deleted here once transcribed."
                  >
                    audio waiting to transfer
                  </span>
                )}
              </div>

              <dl className="flex flex-col gap-[6px] text-[12.5px] leading-none">
                <Fact label="Date">{formatShortDate(lecture.date)}</Fact>
                <Fact label="Course">
                  {lecture.courseCode ?? (
                    <span className="text-muted">Not assigned yet</span>
                  )}
                </Fact>
                {/* Absent fields are ABSENT, not rendered as a dash: an unknown duration and a
                    lecture with no number are both real, and neither is worth a blank row. */}
                {lecture.durationSec !== null && (
                  <Fact label="Length">{clockLength(lecture.durationSec)}</Fact>
                )}
                {lecture.index !== null && <Fact label="Lecture">#{lecture.index}</Fact>}
              </dl>

              <p className="text-[12px] leading-[1.45] text-ink-3 text-pretty">
                {PIPELINE_SENTENCE[lecture.status]}
              </p>

              {lecture.hasTranscript && lecture.courseCode && (
                <Link
                  to={`/school/${courseSlug(lecture.courseCode)}`}
                  style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
                  className="mo-ink mo-press flex min-h-[44px] items-center justify-center rounded-button border border-line bg-surface px-4 text-[13px] font-medium leading-none text-ink-2"
                >
                  Open {lecture.courseCode}
                </Link>
              )}
            </section>

            {/* ── the one write ─────────────────────────────────────────── */}
            <section className="flex flex-col gap-[10px] rounded-card border border-line bg-surface px-4 py-4 shadow-card">
              <h2 className="text-[11px] font-semibold uppercase leading-none tracking-[0.06em] text-muted">
                Assign to a course
              </h2>
              <p className="text-[12px] leading-[1.45] text-ink-3 text-pretty">
                {lecture.courseCode === null
                  ? 'This recording arrived without one — the desktop drop folder knows a filename and nothing else. Its transcript becomes study material for whichever course you pick, so nothing guesses it.'
                  : 'Filing it somewhere else moves its transcript with it. Nothing is regenerated.'}
              </p>

              <select
                value={choice ?? NO_COURSE}
                disabled={assign.isPending}
                onChange={(event) => setChoice(event.target.value)}
                className="min-h-[46px] w-full rounded-button border border-line-2 bg-surface px-3 text-[14px] leading-none text-ink shadow-field"
              >
                <option value={NO_COURSE}>No course</option>
                {(courses ?? []).map((course) => (
                  <option key={course.id} value={course.code}>
                    {course.code} · {course.name}
                  </option>
                ))}
                {/* A course code the connector no longer returns still has to be selectable, or
                    saving anything else would silently drop it. */}
                {lecture.courseCode !== null &&
                  !(courses ?? []).some((course) => course.code === lecture.courseCode) && (
                    <option value={lecture.courseCode}>{lecture.courseCode}</option>
                  )}
              </select>

              {coursesError && (
                <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
                  Your course list could not be read, so only the courses already known here are
                  offered.
                </p>
              )}

              <button
                type="button"
                disabled={!changed || assign.isPending}
                onClick={() => {
                  if (!changed || choice === null) return
                  assign.mutate({ id: lecture.id, courseCode: choice || null })
                }}
                style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
                className={[
                  'min-h-[46px] w-full rounded-button px-4 text-[13px] font-semibold leading-none',
                  !changed || assign.isPending
                    ? 'cursor-not-allowed bg-fill text-faint-2'
                    : 'mo-ink mo-press bg-ink text-on-dark',
                ].join(' ')}
              >
                {assign.isPending
                  ? 'Saving…'
                  : !changed
                    ? 'Already filed here'
                    : choice === NO_COURSE
                      ? 'Unfile this recording'
                      : `File it under ${choice}`}
              </button>

              {assign.error && (
                <p className="rounded-row border border-danger-line bg-danger-fill px-3 py-[9px] text-[12px] leading-[1.45] text-danger text-pretty">
                  {assign.error instanceof Error
                    ? assign.error.message
                    : 'That could not be saved.'}
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <dt className="w-[64px] flex-none text-microcopy">{label}</dt>
      <dd className="min-w-0 flex-1 truncate font-medium text-ink-2">{children}</dd>
    </div>
  )
}

/** What each pipeline state actually means, in the owner's terms. Same words as the 6c screen. */
const PIPELINE_SENTENCE: Record<CapturedLecture['status'], string> = {
  not_recorded:
    'This lecture was not recorded, so there is no audio and no transcript. Nothing failed — the recorder was simply not running.',
  audio_uploaded:
    'The audio is uploaded and queued. It transcribes the next time the desktop PC is awake, and the transcript appears in its course when it does.',
  processing:
    'The desktop PC started transcribing this and has not reported back. The audio is safe; the job runs again when the machine wakes up.',
  transcribed:
    'Transcribed. The transcript, summary and flashcards live on the course’s lecture page.',
  failed:
    'Transcription failed after three attempts. The audio is still safe; the error is on the Connections screen in plain words.',
}
